import React, {
  ReactNode,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import get from "lodash-es/get";
import { Dropdown } from "src/atoms/DropDown";
import { Label } from "src/atoms/Input/Input.styled";
import Input from "src/atoms/Input";
import { FormikProps } from "formik";
import { Checkout } from "src/organisms/CheckoutView/CheckoutStepInfo";
import { Profile } from "src/molecules/ProfileForm/ProfileForm";
import { DropdownProps } from "semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown";
import { callingCountries, lookup } from "country-data-list";
import { FlagIcon } from "src/atoms/FlagIcon";
import { Trans } from "@lingui/macro";
import { PhoneInputContainer, InputWrapper } from "./PhoneInput.styles";
import getConfig from "next/config";
import { DEFAULT_COUNTRY_CODES } from "./constants";
import { StoreKey } from "lib/Intl";
import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

type PhoneInputProps = {
  label: string;
  field: string;
  value?: string;
  formik: FormikProps<Checkout> | FormikProps<Profile>;
  disabled?: boolean;
  autoComplete?: string;
  errorMessage?: ReactNode | string;
};

type CountryData = {
  alpha2: string;
  alpha3: string;
  countryCallingCodes: string[];
  currencies: string[];
  emoji?: string;
  ioc: string;
  languages: string[];
  name: string;
  status: string;
};

const excludedCountries: string[] = ["UM"];
const defaultOrder: string[] = [
  "AT",
  "BE",
  "HR",
  "CZ",
  "DK",
  "FR",
  "DE",
  "HU",
  "IT",
  "LU",
  "NL",
  "PL",
  "PT",
  "SK",
  "SI",
  "ES",
  "SE",
  "GB",
  "US",
];

const { publicRuntimeConfig } = getConfig();
const defaultStoreCountryCode =
  DEFAULT_COUNTRY_CODES[publicRuntimeConfig.STORE_KEY as StoreKey] || "NL";

function PhoneInput({
  label,
  field,
  value,
  formik,
  disabled,
  errorMessage,
  autoComplete,
}: PhoneInputProps): JSX.Element {
  const defaultCountryCode = useMemo(
    () =>
      (formik?.values?.shippingAddress?.country as CountryCode) ||
      defaultStoreCountryCode,
    [formik?.values?.shippingAddress?.country]
  );
  const [countryCode, setCountryCode] = useState<string>(() => {
    const parsedNumber = parsePhoneNumberFromString(
      value || "",
      defaultCountryCode
    );

    if (!value || !parsedNumber || !parsedNumber.country) {
      // Temporary fix due to current typescript version
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      const defaultCallingCode = lookup.countries({
        alpha2: defaultCountryCode.toLowerCase(),
      })?.[0]?.countryCallingCodes[0] as string;

      return `${defaultCountryCode}||${defaultCallingCode}`;
    }

    return `${parsedNumber.country}||+${parsedNumber.countryCallingCode}`;
  });

  const [phoneNumber, setPhoneNumber] = useState<string>(() => {
    if (!value) {
      return "";
    }

    const parsedNumber = parsePhoneNumberFromString(value, defaultCountryCode);

    if (!parsedNumber) {
      return "";
    }

    return value.trim().replace(`+${parsedNumber.countryCallingCode}`, "");
  });

  const options = useMemo(() => {
    const allCallingCountries: CountryData[] =
      callingCountries.all as CountryData[];

    const reducedCallingCountries = allCallingCountries.reduce(
      (acc, country) => {
        if (
          country.countryCallingCodes.length &&
          country.status !== "reserved" &&
          country.status !== "deleted" &&
          !excludedCountries.includes(country.alpha2)
        ) {
          const callingCode = country.countryCallingCodes[0];

          acc.push({
            key: country.alpha2,
            text: (
              <span>
                <FlagIcon countryCode={country.alpha2} />{" "}
                <span>{callingCode}</span>
              </span>
            ),
            value: `${country.alpha2}||${callingCode}`,
          });
        }

        return acc;
      },
      [] as { key: string; text: string | ReactNode; value: string }[]
    );

    const defaultCountries = reducedCallingCountries
      .filter(({ key }) => defaultOrder.includes(key))
      .sort(
        (a, b) => defaultOrder.indexOf(a.key) - defaultOrder.indexOf(b.key)
      );
    const restOfWorld = reducedCallingCountries
      .filter(({ key }) => !defaultOrder.includes(key))
      .sort((a, b) => {
        const aCountryName =
          allCallingCountries.find(({ alpha2 }) => alpha2 === a.key)?.name ||
          "";
        const bCountryName =
          allCallingCountries.find(({ alpha2 }) => alpha2 === b.key)?.name ||
          "";

        if (!aCountryName || !bCountryName) {
          return 0;
        }

        return aCountryName.localeCompare(bCountryName);
      });

    return [...defaultCountries, ...restOfWorld];
  }, []);

  const handleOnCountryCodeChange = useCallback(
    (_event: SyntheticEvent<HTMLElement>, stateData: DropdownProps) => {
      const currentValue: string = (stateData.value as string) || "";
      setCountryCode(currentValue);

      if (!phoneNumber.trim()) {
        return;
      }

      const countryCodeValue = currentValue.split("||")[1] || "";

      void formik.setFieldValue(
        field,
        countryCodeValue.trim() + phoneNumber.trim()
      );
    },
    [field, formik, phoneNumber]
  );

  const handleOnPhoneNumberChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(event.target.value);
    },
    []
  );

  const handleOnPhoneNumberBlur = useCallback(() => {
    const countryCodeValue = countryCode.split("||")[1] || "";
    const trimmedNumber = countryCodeValue.trim() + phoneNumber.trim();

    if (!phoneNumber) {
      void formik.setFieldValue(field, "");
      return;
    }

    void formik.setFieldValue(field, trimmedNumber);
  }, [formik, field, countryCode, phoneNumber]);

  return (
    <PhoneInputContainer>
      <InputWrapper>
        {
          <Label px={1}>
            <Trans>country_code</Trans>
          </Label>
        }
        <Dropdown
          value={countryCode}
          onChange={handleOnCountryCodeChange}
          disabled={disabled}
          options={options}
          className="less-padding"
        />
      </InputWrapper>
      <InputWrapper>
        {<Label px={1}>{label}</Label>}

        <Input
          type="tel"
          label={get(formik.values, field) ? label : ""}
          placeholder={label}
          disabled={disabled}
          value={phoneNumber}
          onChange={handleOnPhoneNumberChange}
          onBlur={handleOnPhoneNumberBlur}
          errorMessage={errorMessage}
          {...(autoComplete ? { autoComplete } : {})}
        />
      </InputWrapper>
    </PhoneInputContainer>
  );
}

export { PhoneInput };
